import { Icon, Tooltip, Typography } from "@/components/atoms";
import { useAppSelector } from "@/redux/hooks";
import { configSelector } from "@/redux/slices/config/selectors";

/**
 * Info tooltip for the "Credit Card Payments" sub-header (Figma 5379-29655):
 * dark panel with the card management & security notes, operator name substituted.
 */
export const CardSecurityTooltip = () => {
  const operatorName = useAppSelector(configSelector)?.name || "The operator";

  const bullets = [
    "You cannot make a booking without a valid registered credit card.",
    `${operatorName} does not have access to your credit card details at any time - you are registering your credit card directly with Stripe via a secure encrypted link.`,
    "To update your credit card details you must first remove that card and then add a new card with the new details.",
    `${operatorName} will perform fraud checks (including a small pre-authorization) upon registering your card to check validity.`,
    `${operatorName} reserves the right to collect payment prior to a trip for new clients, special and one-off bookings.`,
    `${operatorName} is not able to reverse payments made to an incorrect card. Please double-check before booking.`,
  ];

  return (
    <Tooltip
      interactive
      placement="bottom-start"
      maxWidth={560}
      className="!rounded-lg !bg-neutral-black [&>.tippy-arrow]:!text-neutral-black"
      content={
        <div className="flex flex-col gap-1 p-1.5 text-sm leading-4 text-white">
          <p className="font-semibold">Manage your cards &amp; credit card security</p>
          <ul className="list-disc pl-4 font-medium">
            {bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
            <li>
              Please visit Stripe for more information on your card security:{" "}
              <a href="https://stripe.com/" target="_blank" rel="noreferrer" className="underline">
                https://stripe.com/
              </a>
            </li>
          </ul>
        </div>
      }
    >
      <span
        tabIndex={0}
        role="button"
        className="w-fit cursor-pointer self-start text-neutral-dark-gray"
      >
        <Typography variant="small-regular" className="!inline underline decoration-dotted underline-offset-2">
          How card security &amp; post-trip charges work — read more
        </Typography>{" "}
        <Icon name="InfoCircle" variant="Outline" size={16} className="inline align-middle text-neutral-dark-gray" />
      </span>
    </Tooltip>
  );
};
